import { useAuth, useSignIn, useSignUp } from '@clerk/nextjs';
import { ClerkAPIError } from '@clerk/types';
import { captureException } from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { FRONTEND_DOMAIN } from 'utils/env.utils';
import { addLocalePrefix } from 'utils/routes.utils';

import { SiteLocale } from 'constants/i18n.constants';
import { Routes } from 'constants/routes.constants';
import { useLocale } from 'hooks/useLocale';
import { QueryParam } from 'types/auth.types';

const createFrontendUrlWithParams = (
  afterSignInUrl: string,
  emailAddress: string,
  locale: SiteLocale,
) => {
  const frontendUrl = new URL(FRONTEND_DOMAIN);
  frontendUrl.pathname = addLocalePrefix(locale, Routes.EmailLinkVerification);
  frontendUrl.searchParams.set(QueryParam.ReturnUrl, afterSignInUrl);
  frontendUrl.searchParams.set(QueryParam.EmailAddress, emailAddress);
  return frontendUrl;
};

type ClerkApiErrorResponse = {
  errors: Array<ClerkAPIError>;
};

interface Props {
  emailAddress: string;
  afterSignInUrl: string;
}

export const useEmailLinkFlow = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailSendError, setEmailSendError] = useState(false);
  const { isLoaded } = useAuth();
  const { signIn, setActive: signInSetActive } = useSignIn();
  const signIncreateEmailLinkFlow = signIn?.createEmailLinkFlow();
  const { signUp, setActive: signUpActive } = useSignUp();
  const signUpcreateEmailLinkFlow = signUp?.createEmailLinkFlow();
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const router = useRouter();
  const { locale } = useLocale();

  // Use a 1s timeout to delay showing success until after polling resolves or to show an error if polling fails.
  useEffect(() => {
    if (emailSent && !emailSendError) {
      const timer = setTimeout(() => {
        enqueueSnackbar(
          intl.formatMessage({ id: 'account.resend.email.success.alert' }),
          { variant: 'success' },
        );
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [emailSent, emailSendError, enqueueSnackbar, intl]);

  const handleEmailSendError = (error: unknown) => {
    setEmailSendError(true);
    setEmailSent(false);
    setIsLoading(false);
    captureException(error, {
      tags: {
        service: 'email-link',
        action: 'handleEmailSendError',
      },
    });
  };

  //   We first try sign in, if that fails we try sign up. (clerk does not handle that automatically)
  const handleEmailLinkSignInorSignup = async ({
    emailAddress,
    afterSignInUrl,
  }: Props) => {
    if (
      !isLoaded ||
      !signIn ||
      !signUp ||
      !signIncreateEmailLinkFlow ||
      !signUpcreateEmailLinkFlow
    )
      return;

    setIsLoading(true);
    setEmailSendError(false);
    setEmailSent(false);

    const frontendUrl = createFrontendUrlWithParams(
      afterSignInUrl,
      emailAddress,
      locale,
    );

    try {
      const signInObject = await signIn.create({ identifier: emailAddress });

      const signInFirstFactor = signInObject.supportedFirstFactors?.find(
        (ff) =>
          ff.strategy === 'email_link' &&
          ff.safeIdentifier === emailAddress?.toLowerCase(),
      );

      // Bail if we couldn't find a supported first factor
      if (!signInFirstFactor || signInFirstFactor?.strategy !== 'email_link') {
        setIsLoading(false);
        setEmailSent(false);
        return;
      }

      setEmailSent(true);
      setIsLoading(false);

      const pollingResponse =
        await signIncreateEmailLinkFlow.startEmailLinkFlow({
          emailAddressId: signInFirstFactor.emailAddressId,
          redirectUrl: frontendUrl.href,
        });

      if (pollingResponse.status === 'complete') {
        await signInSetActive({
          session: pollingResponse.createdSessionId,
          redirectUrl: afterSignInUrl,
        });
      }
      return;
    } catch (error) {
      const clerkAPIError = (error as ClerkApiErrorResponse)?.errors ?? [];
      const clerkError = clerkAPIError[0]?.code;

      if (clerkError === 'too_many_requests') {
        signIncreateEmailLinkFlow.cancelEmailLinkFlow();
        enqueueSnackbar(
          intl.formatMessage({
            id: 'account.resend.email.too.many.requests.error.alert',
          }),
          { variant: 'error' },
        );
        handleEmailSendError(error);
      }

      if (clerkError === 'session_exists') {
        signIncreateEmailLinkFlow.cancelEmailLinkFlow();
        handleEmailSendError(error);
        router.reload();
      }

      if (clerkError === 'form_identifier_not_found') {
        try {
          await signUp.create({ emailAddress });
          setEmailSent(true);
          setIsLoading(false);

          const pollingResponse =
            await signUpcreateEmailLinkFlow.startEmailLinkFlow({
              redirectUrl: frontendUrl.href,
            });

          if (pollingResponse.status === 'complete') {
            await signUpActive({
              session: pollingResponse.createdSessionId,
              redirectUrl: afterSignInUrl,
            });
          }
          return;
        } catch (error) {
          signUpcreateEmailLinkFlow.cancelEmailLinkFlow();
          handleEmailSendError(error);
        }
      } else {
        signIncreateEmailLinkFlow.cancelEmailLinkFlow();
        handleEmailSendError(error);
      }
    }
  };

  const handleReset = () => {
    setEmailSent(false);
    setEmailSendError(false);
    setIsLoading(false);
  };

  return {
    isLoading,
    emailSent,
    onEmailLinkSignInOrSignup: handleEmailLinkSignInorSignup,
    reset: handleReset,
  };
};
